import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

type ProjectRole = "admin" | "deploy" | "view";

interface ProjectMember {
  userId: string;
  email: string;
  role: ProjectRole;
}
interface OrgMember {
  userId: string;
  email: string;
  role: "owner" | "admin" | "member";
}

/**
 * T120 — per-project grants. Lists current explicit grants and lets an
 * org admin add/edit/remove. Org owners/admins implicitly have project
 * admin everywhere, so they're shown as "implicit admin" rather than as
 * grantable rows.
 */
export function ProjectMembers() {
  const { org, project } = useParams<{ org: string; project: string }>();
  const qc = useQueryClient();
  const [addUserId, setAddUserId] = useState("");
  const [addRole, setAddRole] = useState<ProjectRole>("view");

  const grants = useQuery({
    queryKey: ["project-members", org, project],
    queryFn: () =>
      api
        .get<{ members: ProjectMember[] }>(`/orgs/${org}/projects/${project}/members`)
        .then((r) => r.members),
  });
  const orgMembers = useQuery({
    queryKey: ["org-members", org],
    queryFn: () => api.get<{ members: OrgMember[] }>(`/orgs/${org}/members`).then((r) => r.members),
  });

  const grantable = useMemo(() => {
    const grantedIds = new Set((grants.data ?? []).map((g) => g.userId));
    return (orgMembers.data ?? []).filter((m) => m.role === "member" && !grantedIds.has(m.userId));
  }, [grants.data, orgMembers.data]);

  const upsert = useMutation({
    mutationFn: () =>
      api.post(`/orgs/${org}/projects/${project}/members`, { userId: addUserId, role: addRole }),
    onSuccess: () => {
      setAddUserId("");
      qc.invalidateQueries({ queryKey: ["project-members", org, project] });
    },
  });
  const change = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: ProjectRole }) =>
      api.patch(`/orgs/${org}/projects/${project}/members/${userId}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project-members", org, project] }),
  });
  const remove = useMutation({
    mutationFn: (userId: string) =>
      api.delete(`/orgs/${org}/projects/${project}/members/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project-members", org, project] }),
  });

  const implicitAdmins = (orgMembers.data ?? []).filter((m) => m.role !== "member");

  return (
    <div className="space-y-5">
      <Section
        title="Project grants"
        subtitle="Org owners/admins have implicit admin everywhere; this is for plain org members."
      >
        {implicitAdmins.length > 0 && (
          <div className="divide-y divide-border-muted">
            {implicitAdmins.map((m) => (
              <div key={m.userId} className="of-row gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm font-semibold truncate">{m.email}</div>
                </div>
                <span className="text-2xs uppercase tracking-wider font-mono bg-subtle border border-border-muted rounded px-1.5 py-0.5">
                  implicit admin ({m.role})
                </span>
              </div>
            ))}
          </div>
        )}
        {(grants.data ?? []).map((g) => (
          <div key={g.userId} className="of-row gap-3">
            <div className="min-w-0 flex-1">
              <div className="font-mono text-sm font-semibold truncate">{g.email}</div>
            </div>
            <select
              value={g.role}
              onChange={(e) => change.mutate({ userId: g.userId, role: e.target.value as ProjectRole })}
              disabled={change.isPending}
              className="h-8 px-2 rounded-md border border-border bg-background text-xs font-mono"
            >
              <option value="view">view</option>
              <option value="deploy">deploy</option>
              <option value="admin">admin</option>
            </select>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                if (confirm(`Revoke ${g.email}'s project grant?`)) remove.mutate(g.userId);
              }}
              disabled={remove.isPending}
              className="text-danger"
            >
              {remove.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
            </Button>
          </div>
        ))}
        {(grants.data?.length ?? 0) === 0 && implicitAdmins.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No grants yet. Add org members below to give them access.
          </div>
        )}
      </Section>

      <Section title="Add grant">
        <div className="px-4 py-3 space-y-2">
          {grantable.length === 0 ? (
            <div className="text-xs text-muted-foreground">
              All plain-member org members already have a grant. Invite more from <span className="font-mono">Org → Members</span>.
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={addUserId}
                  onChange={(e) => setAddUserId(e.target.value)}
                  className="h-9 px-2 rounded-md border border-border bg-background text-sm font-mono flex-1 min-w-[200px]"
                >
                  <option value="">— pick org member —</option>
                  {grantable.map((m) => (
                    <option key={m.userId} value={m.userId}>{m.email}</option>
                  ))}
                </select>
                <select
                  value={addRole}
                  onChange={(e) => setAddRole(e.target.value as ProjectRole)}
                  className="h-9 px-2 rounded-md border border-border bg-background text-sm font-mono"
                >
                  <option value="view">view</option>
                  <option value="deploy">deploy</option>
                  <option value="admin">admin</option>
                </select>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => upsert.mutate()}
                  disabled={!addUserId || upsert.isPending}
                >
                  {upsert.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                  Add
                </Button>
              </div>
              {upsert.error instanceof ApiError && <div className="text-xs text-danger">{upsert.error.message}</div>}
            </>
          )}
        </div>
      </Section>
    </div>
  );
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="of-card">
      <div className="of-card-header">
        <h3 className="text-sm font-semibold">{title}</h3>
        {subtitle && <span className="hidden sm:inline text-2xs text-subtle-foreground">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}
